/**
 * Per-paint projection of composition text fields under a non-original
 * caption language (PLAT-9966).
 *
 * Walks `videoSourceElements` looking for composition placements
 * (`type === 'composition'`), pairs each placement with its
 * `extraElementData[compositionElementId]` bucket (template id + parsed
 * controls), enumerates every translatable control, and rewrites the
 * resolved source element's text property when the resolver (story phrase
 * or template preset) produces a different value.
 *
 * The walker structure intentionally mirrors `extractTranslatableElements`
 * so the two stay in lockstep: an element flagged translatable on the
 * authoring side that survives extraction will also survive projection.
 *
 * Returns the input `videoSourceElements` reference unchanged when nothing
 * needed rewriting (and when active language is original); otherwise
 * returns a structurally-cloned subtree that shares element references
 * with the input wherever no rewrite landed. Callers can rely on
 * reference equality as a cheap "nothing changed" signal.
 *
 * The returned `rewrites` array captures each rewrite's canonical
 * original-language value. VideoSourceStore uses it to un-project the
 * renderer's source-readback (`renderer.getSource()` → `_source`) so the
 * canonical store stays free of translated text. Without un-projection,
 * the renderer would round-trip the projected payload back into `_source`,
 * polluting the save path and breaking the "switch back to Original" path.
 */
import { validateTranslationFlags } from '@src/components/sidepanel/compositionNormalizer/validateTranslationFlags';
import { isOriginalLang } from '@src/utility/captionLanguage';

import { setLeafPropertyByPath, traverseElementPath } from './elementPathUtils';
import { resolveCompositionFieldText } from './resolveCompositionFieldText';

import type { CompositionPhraseTranslation } from './compositionTypes';
import type { ControlConfiguration } from '@src/components/sidepanel/CompositionSettings/types';

export interface ProjectParams {
  videoSourceElements:
    | ReadonlyArray<Record<string, unknown>>
    | null
    | undefined;
  extraElementData: Record<string, unknown> | null | undefined;
  activeLanguage: string;
  compositionPhrasesByNormalizedSource: ReadonlyMap<
    string,
    CompositionPhraseTranslation
  >;
  /**
   * PLAT-11466 — the font a translated element should render in, or `null` to
   * keep its own. Injected (rather than imported) so this module stays a pure
   * projection and the workspace's brand-font coverage is the caller's
   * concern; omitting it keeps the pre-PLAT-11466 behaviour of never touching
   * fonts. Callers pass `getGraphicFontSubstitute` bound to their brand-font
   * lookup.
   */
  resolveFontSubstitute?: (
    fontFamily: string,
    targetLanguage: string,
  ) => string | null;
}

export interface ProjectionRewrite {
  /** Top-level composition placement id. */
  placementId: string;
  /** Path from the placement to the rewritten element (control's `elementPaths[0]`). */
  path: ReadonlyArray<number>;
  /** Property on the leaf element we rewrote (`"text"` in v1). */
  property: string;
  /** Canonical original-language value, captured for un-projection. */
  originalValue: string;
}

export interface ProjectResult {
  /**
   * Projected elements ready to ship to the renderer. Same reference as
   * the input when nothing changed; structurally cloned otherwise.
   */
  elements: ReadonlyArray<Record<string, unknown>>;
  /** True when at least one text field was rewritten. */
  changed: boolean;
  /** Diagnostic count of fields rewritten in this pass. */
  rewriteCount: number;
  /**
   * Rewrites emitted by this pass. Consumed by VideoSourceStore to
   * un-project the renderer's source-readback. Empty when changed=false.
   */
  rewrites: ReadonlyArray<ProjectionRewrite>;
}

export function projectCompositionTranslations(
  params: ProjectParams,
): ProjectResult {
  const {
    videoSourceElements,
    extraElementData,
    activeLanguage,
    compositionPhrasesByNormalizedSource,
    resolveFontSubstitute,
  } = params;

  if (
    !Array.isArray(videoSourceElements) ||
    videoSourceElements.length === 0 ||
    isOriginalLang(activeLanguage)
  ) {
    return {
      elements: videoSourceElements ?? [],
      changed: false,
      rewriteCount: 0,
      rewrites: [],
    };
  }

  let rewriteCount = 0;
  let nextTop: Array<Record<string, unknown>> | null = null;
  const rewritesEmitted: ProjectionRewrite[] = [];

  for (let i = 0; i < videoSourceElements.length; i++) {
    const candidate = videoSourceElements[i];
    if (!candidate || typeof candidate !== 'object') continue;
    const elementType = (candidate as { type?: unknown }).type;
    if (elementType !== 'composition') continue;
    const compositionElementId = (candidate as { id?: unknown }).id;
    if (typeof compositionElementId !== 'string' || compositionElementId === '')
      continue;

    const meta = readMeta(extraElementData, compositionElementId);
    if (!meta) continue;
    const { sanitized } = validateTranslationFlags(meta.controls);

    // Collect rewrites for this placement; apply on a single clone so deeply
    // nested elements get cloned at most once even when multiple controls
    // target sibling source elements.
    const placementRewrites: Array<{
      path: ReadonlyArray<number>;
      property: string;
      newValue: string;
      originalValue: string;
    }> = [];

    for (const control of sanitized) {
      if (!control.allowTranslation) continue;
      if (
        !Array.isArray(control.elementPaths) ||
        control.elementPaths.length === 0
      )
        continue;
      // Multi-path is demoted by the validator + skipped by the extractor;
      // mirror that policy here.
      if (control.elementPaths.length > 1) continue;

      const path = control.elementPaths[0];
      const leaf = traverseElementPath(candidate, path);
      if (!leaf) continue;
      const value = leaf[control.property];
      if (typeof value !== 'string' || value.trim() === '') continue;

      const controlPreset =
        typeof control.default?.value === 'string' &&
        control.default.value !== ''
          ? {
              templateDefaultValue: control.default.value,
              translations: control.translations,
            }
          : undefined;

      const resolved = resolveCompositionFieldText({
        currentValue: value,
        activeLanguage,
        compositionPhrasesByNormalizedSource,
        controlPreset,
      });

      if (resolved !== value) {
        placementRewrites.push({
          path,
          property: control.property,
          newValue: resolved,
          originalValue: value,
        });
        // PLAT-11466 — the text we just wrote may need glyphs this element's
        // font does not have. Emitted as its own rewrite on the SAME leaf, so
        // un-projection restores the original font exactly as it restores the
        // original text (a projected font must never reach the save path).
        const currentFont = leaf.font_family;
        if (typeof currentFont === 'string' && currentFont !== '') {
          const substitute = resolveFontSubstitute?.(
            currentFont,
            activeLanguage,
          );
          if (substitute && substitute !== currentFont) {
            placementRewrites.push({
              path,
              property: 'font_family',
              newValue: substitute,
              originalValue: currentFont,
            });
          }
        }
      }
    }

    if (placementRewrites.length === 0) continue;

    if (!nextTop) {
      nextTop = videoSourceElements.slice() as Array<Record<string, unknown>>;
    }
    let projectedPlacement: Record<string, unknown> = candidate;
    for (const r of placementRewrites) {
      const next = setLeafPropertyByPath(
        projectedPlacement,
        r.path,
        r.property,
        r.newValue,
      );
      if (next) {
        projectedPlacement = next;
        rewriteCount += 1;
        rewritesEmitted.push({
          placementId: compositionElementId,
          path: r.path,
          property: r.property,
          originalValue: r.originalValue,
        });
      }
    }
    nextTop[i] = projectedPlacement;
  }

  if (!nextTop) {
    return {
      elements: videoSourceElements,
      changed: false,
      rewriteCount: 0,
      rewrites: [],
    };
  }

  return {
    elements: nextTop,
    changed: true,
    rewriteCount,
    rewrites: rewritesEmitted,
  };
}

interface CompositionMeta {
  templateId: string;
  controls: ControlConfiguration[];
}

function readMeta(
  extraElementData: Record<string, unknown> | null | undefined,
  compositionElementId: string,
): CompositionMeta | null {
  if (!extraElementData || typeof extraElementData !== 'object') return null;
  const bucket = (extraElementData as Record<string, unknown>)[
    compositionElementId
  ];
  if (!bucket || typeof bucket !== 'object') return null;
  const templateId = (bucket as { templateId?: unknown }).templateId;
  const controls = (bucket as { compositionControls?: unknown })
    .compositionControls;
  if (typeof templateId !== 'string' || templateId === '') return null;
  if (!Array.isArray(controls)) return null;
  return {
    templateId,
    controls: controls as ControlConfiguration[],
  };
}
